import { yupResolver } from "@hookform/resolvers/yup";
import {
  Checkbox,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { addTax, getTax, updateTax } from "src/apis/TaxApis";
import { GetLocations } from "src/apis/locationApis";
import { useProfile } from "src/auth/Auth";
import Permissions from "src/common_constants/Permission";
import useFetch from "src/hooks/useFetch";
import useHandleSubmit from "src/hooks/useHandleSubmit";
import EditPageWrapper from "src/shared_components/EditPageWrapper";
import { InputField, SelectField } from "src/shared_components/FormElements";
import PermissionRestrictedComponent from "src/shared_components/PermissionRestrictedComponent";
import AddClearButtons from "src/shared_components/buttons/AddClearButtons";
import { TaxDto } from "src/shared_components/interfaces";
import Swal from "sweetalert2";
import * as yup from "yup";

interface TaxManagementFormProps {
  id?: number | null;
  onClose?: () => void;
  refreshData?: () => void;
}

const TaxManagementForm: React.FC<TaxManagementFormProps> = ({
  id,
  onClose,
  refreshData,
}) => {
  const { clientId } = useProfile();
  const { t } = useTranslation();
  const [locationLoading, setLocationLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const requiredMessage = (field: string) =>
    t("common.messages.validationErrors.required", {
      label: t(`settings.client.form.inputs.labels.${field}`),
    });

  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .max(
        50,
        t("common.messages.validationErrors.maxLength", { maxLength: 50 })
      )
      .required(requiredMessage("name")),

    taxNumber: yup.string(),

    description: yup
      .string()
      .required(requiredMessage("description"))
      .max(
        255,
        t("common.messages.validationErrors.maxLength", { maxLength: 255 })
      ),

    defaultTaxValue: yup
      .number()
      .transform((value, originalValue) => {
        // Convert empty string to undefined so .required() works
        return originalValue === "" ? undefined : value;
      })
      .typeError(t("common.messages.validationErrors.numbervalidation"))
      .required(requiredMessage("defaultTaxValue"))
      .test(
        "is-decimal",
        t("common.messages.validationErrors.numbers.invalidDecimal", {
          value: 2,
        }),
        (value) => {
          if (value !== undefined && value !== null) {
            const decimalCount = (value.toString().split(".")[1] || "").length;
            return decimalCount <= 2;
          }
          return true;
        }
      )
      .positive(t("common.messages.validationErrors.numbers.positiveNumber"))
      .max(
        100,
        t("common.messages.validationErrors.numbers.max", {
          value: 100,
        })
      ),
    isItGlobal: yup.boolean(),
    isDefault: yup.boolean(),
    locationId: yup
      .number()
      .typeError(t("common.messages.validationErrors.chooseOneRequired"))
      .nullable()
      .transform((value, originalValue) => {
        if (!originalValue) return null;
        return value;
      }),

    applyToAddOns: yup.boolean().default(false),
    applyToRatePlan: yup.boolean().default(false),
  });

  const methods = useForm<TaxDto>({
    mode: "onChange",
    resolver: yupResolver<TaxDto>(validationSchema),
    defaultValues: {
      isItGlobal: true,
      isDefault: false,
    },
  });

  const {
    control,
    setValue,
    register,
    getValues,
    watch,
    formState: { errors, isSubmitting },
  } = methods;

  const [locations, loading] = useFetch(
    () => GetLocations(`${clientId}`),
    [],
    true
  );

  const [_, isLoading] = useFetch(
    () => getTax(id),
    [id],
    !!id,
    (data) => {
      setValue("name", data.name);
      setValue("description", data.description || "");
      setValue("locationId", data.locationId || "");
      setValue("taxNumber", data.taxNumber || "");
      setValue("defaultTaxValue", data.defaultTaxValue || "");
      setValue("isItGlobal", data.locationId > 0 ? false : true);
      setValue("applyToRatePlan", data.applyToRatePlan);
      setValue("applyToAddOns", data.applyToAddOns);
      setValue("isDefault", data.isDefault ?? false);
    }
  );

  const onSubmit = useHandleSubmit<TaxDto>({
    id: id ?? undefined,
    addApiFunction: (values) => addTax(values),
    updateApiFunction: (id, values) => updateTax(id, values),
    moduleNameKey: "common.apiMsgs.moduleNames.tax",
    callBackFn: (values) => {
      void (async () => {
        onClose?.();

        if (!id) {
          const result = await Swal.fire({
            title: "Tax Mapping",
            text: "Do you want to map this tax for add-ons and  rate name?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes",
            cancelButtonText: "No",
          });

          if (result.isConfirmed) {
            navigate("/Settings/Client/TaxMapping");
            return;
          }
        }

        refreshData?.();
      })();
    },
  });

  const locationIdValue = watch("locationId");
  const isItGlobalValue = watch("isItGlobal");

  return (
    <FormProvider {...methods}>
      <EditPageWrapper
        id={id ?? undefined}
        loading={loading || locationLoading || isLoading}
        onSubmit={onSubmit}
        editTitle=""
        addTitle=""
        createOrModifyPermission={Permissions.AssetRateCreateModify}
        isSubmitBtn={false}
      >
        <Grid container spacing={3} pt={2}>
          <Grid item xl={4} md={6} xs={12}>
            <InputField
              fieldName="name"
              control={control}
              label={t("settings.client.form.inputs.labels.name")}
              fullWidth
              yupSchema={validationSchema}
              error={!!errors?.name}
              helperText={errors?.name?.message}
            />
          </Grid>
          <Grid item xl={4} md={6} xs={12}>
            <InputField
              fieldName="taxNumber"
              control={control}
              label={t("settings.client.form.inputs.labels.taxNumber")}
              fullWidth
              yupSchema={validationSchema}
              error={!!errors?.taxNumber}
              helperText={errors?.taxNumber?.message}
            />
          </Grid>
          <Grid item xl={4} md={6} xs={12}>
            <InputField
              fieldName="defaultTaxValue"
              control={control}
              label={t("settings.client.form.inputs.labels.defaultTaxValue")}
              fullWidth
              yupSchema={validationSchema}
              required
              error={!!errors?.defaultTaxValue}
              helperText={errors?.defaultTaxValue?.message}
            />
          </Grid>

          <Grid item xl={12} md={6} xs={12}>
            <InputField
              fieldName="description"
              control={control}
              label={t("settings.client.form.inputs.labels.description")}
              fullWidth
              yupSchema={validationSchema}
              error={!!errors?.description}
              helperText={errors?.description?.message}
              multiline
              other={{
                rows: 3,
              }}
            />
          </Grid>
          <Grid item xl={4} md={6} xs={12}>
            <Stack direction={"column"} gap={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    defaultChecked={getValues("isDefault")}
                    {...register("isDefault")}
                    size="small"
                  />
                }
                name="isDefault"
                label={
                  <Typography variant="body2" color="text.main">
                    {t("settings.rate.form.inputs.labels.isDefault")}
                  </Typography>
                }
              />
            </Stack>
          </Grid>

          <PermissionRestrictedComponent permission={Permissions.LocationView}>
            <Grid item xl={4} md={6} xs={12}>
              <FormControlLabel
                // sx={{
                //   pointerEvents:
                //     id && (!!locationIdValue || isItGlobalValue)
                //       ? "none"
                //       : "auto",
                // }}
                disabled={!!id && (!!locationIdValue || isItGlobalValue)}
                control={
                  <Checkbox
                    checked={!!isItGlobalValue}
                    {...register("isItGlobal")}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" color="text.main">
                    {t("settings.client.form.inputs.labels.globalTax")}
                  </Typography>
                }
              />

              {!isItGlobalValue ? (
                <SelectField
                  fieldName="locationId"
                  control={control}
                  label={t("settings.client.form.inputs.labels.locationId")}
                  options={locations}
                  yupSchema={validationSchema}
                  error={!!errors?.locationId}
                  showEmptyOption={false}
                  other={
                    {
                      // sx: {pointerEvents: !!locationIdValue ? "auto" : "none" },
                    }
                  }
                  valueKey="locationId"
                  labelKey="name"
                  helperText={errors?.locationId?.message}
                  showMissingDeleted={true}
                  currentValue={getValues(`locationId`)}
                />
              ) : null}
            </Grid>
          </PermissionRestrictedComponent>
          <Grid container justifyContent="flex-end">
            <AddClearButtons callBackFunc={onClose} disabled={isSubmitting} />
          </Grid>
        </Grid>
      </EditPageWrapper>
    </FormProvider>
  );
};

export default TaxManagementForm;
